import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import { Warehouse, ChevronRight, Users, FileText, Trophy } from 'lucide-react';
import { api, type TrendingGarage } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import { trackGarageAddClick, trackGarageSpotlightImpression, trackGarageSpotlightClick } from '@/lib/analytics';

function MostActiveGarage({ garage }: { garage: TrendingGarage }) {
  return (
    <div className="px-3 py-3 border-b border-border-primary">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-400 mb-2">
        <Trophy className="h-3.5 w-3.5" />
        Most Active This Week
      </div>
      <Link
        to={`/garage/${garage.slug}`}
        className="group block"
      >
        <div className="flex items-center gap-3">
          {garage.imageUrl ? (
            <img
              src={garage.imageUrl}
              alt=""
              className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-violet-500/10 text-lg">
              {garage.emoji || '🏠'}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text-primary group-hover:text-violet-400 transition-colors truncate">
              {garage.name}
            </p>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-text-tertiary">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {formatNumber(garage.memberCount)} members
              </span>
              <span className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {garage.newPosts} posts this week
              </span>
            </div>
          </div>
        </div>
      </Link>
      <Link
        to={`/garage/${garage.slug}`}
        onClick={() => { trackGarageAddClick(garage.id, 'garage_spotlight'); trackGarageSpotlightClick(garage.id, garage.name); }}
        className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-400 ring-1 ring-violet-500/25 transition-all hover:bg-violet-500/20 hover:ring-violet-500/40"
      >
        View &amp; Join Garage
        <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

export function GarageSpotlight() {
  const { data, isLoading } = useQuery({
    queryKey: ['garages', 'spotlight'],
    queryFn: () => api.garages.spotlight({ limit: 3 }),
    staleTime: 30 * 60 * 1000, // 30 min (backend caches 30 min)
  });

  const { data: trendingData } = useQuery({
    queryKey: ['garages', 'trending', 1],
    queryFn: () => api.garages.trending({ limit: 1, days: 7 }),
    staleTime: 60 * 60 * 1000, // 1 hour (backend caches 1 hour)
  });

  const topGarage = trendingData?.items?.[0];

  // ANALYTICS-076: Track spotlight impression once visible
  const spotlightRef = useRef<HTMLDivElement>(null);
  const impressionFired = useRef(false);
  useEffect(() => {
    const el = spotlightRef.current;
    if (!el || !topGarage || impressionFired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressionFired.current) {
          impressionFired.current = true;
          trackGarageSpotlightImpression(topGarage.id, topGarage.name);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [topGarage]);

  // Don't render if no entries available (avoids empty widget)
  if (!isLoading && (!data?.items || data.items.length === 0) && !topGarage) return null;

  return (
    <div ref={spotlightRef} className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-violet-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10">
          <Warehouse className="h-4 w-4 text-violet-500" />
        </div>
        <h3 className="font-semibold text-text-primary">Garage Spotlight</h3>
      </div>

      {topGarage && <MostActiveGarage garage={topGarage} />}

      <div className="p-2">
        {isLoading ? (
          <div className="space-y-3 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-3 w-32 rounded bg-bg-tertiary" />
                <div className="mt-1.5 h-4 w-full rounded bg-bg-tertiary" />
              </div>
            ))}
          </div>
        ) : (
          data?.items.map((entry) => (
            <Link
              key={entry.id}
              to={entry.post ? `/post/${entry.post.id}` : `/garage/${entry.garage.slug}`}
              className="flex gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
            >
              {entry.post?.imageUrl && (
                <img
                  src={entry.post.imageUrl}
                  alt=""
                  className="h-12 w-12 flex-shrink-0 rounded-md object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
                  <span>{entry.garage.emoji || '🏠'}</span>
                  <Link
                    to={`/garage/${entry.garage.slug}`}
                    className="truncate font-medium hover:text-violet-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {entry.garage.name}
                  </Link>
                  <span>·</span>
                  <span className="truncate">{entry.user.displayName || entry.user.username}</span>
                </div>
                <p className="mt-0.5 text-sm font-medium text-text-secondary group-hover:text-primary-400 transition-colors line-clamp-2">
                  {entry.post?.title || entry.title || 'Shared a post'}
                </p>
                {entry.post?.source?.name && (
                  <span className="mt-0.5 text-xs text-text-tertiary">
                    via {entry.post.source.name}
                  </span>
                )}
              </div>
            </Link>
          ))
        )}
      </div>

      <Link
        to="/explore?tab=garages"
        className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
      >
        Explore garages
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
